import { FC, KeyboardEvent, SyntheticEvent, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { EasTooltip } from 'ui/EasTooltip';
import { getUserInfo } from 'redux/reducers/user/selectors';
import { selectRealmRoles } from 'redux/reducers/auth/selectors';
import { filterNav, isPathAllowed, PATHS } from 'routes/constants';
import { PLAIN_TABS, TAB_GROUPS } from 'components/ReferenceTables/constants';
import { PAYMENT_GATEWAY_SEARCH_PAGES } from 'components/PaymentGateway/constants';
import { NAV_ITEM_TOUR_DESCRIPTIONS } from '../constants';

type TSearchPage = {
	name: string;
	path: string;
	description?: string;
};

// The Reference Tables page (routes/constants.ts) only lists a handful of its
// tabs as standalone nav entries; the rest live in the tab bar's own config.
// Those duplicates are dropped from the nav-derived list below and replaced
// by the full tab set so every reference table is searchable.
const REFERENCE_TABLES_NAV_DUPLICATE_PATHS = new Set([
	`${PATHS.REFERENCE_TABLES_PAGE}/non-eu-vat-rates`,
	`${PATHS.REFERENCE_TABLES_PAGE}/shopify-sales-channels`,
]);

const REFERENCE_TABLE_PAGES: TSearchPage[] = [
	...PLAIN_TABS.map((tab) => ({
		name: tab.label,
		path: `${PATHS.REFERENCE_TABLES_PAGE}/${tab.value}`,
		description: 'Reference Tables',
	})),
	...TAB_GROUPS.flatMap((group) =>
		group.items.map((item) => ({
			name: item.label,
			path: `${PATHS.REFERENCE_TABLES_PAGE}/${item.value}`,
			description: `Reference Tables · ${group.name}`,
		})),
	),
];

// Also match against each page's description, so e.g. "Superset" finds
// "Dashboards" or "Fees" finds "Fee Classes".
const filterOptions = createFilterOptions<TSearchPage>({
	stringify: (option) => `${option.name} ${option.description ?? ''}`,
});

export const PageSearch: FC = () => {
	const navigate = useNavigate();
	const userinfo = useSelector(getUserInfo);
	const roles = userinfo?.roles;
	const realmRoles = useSelector(selectRealmRoles);

	const [active, setActive] = useState(false);

	const pages = useMemo<TSearchPage[]>(() => {
		if ((!roles || roles.length === 0) && realmRoles.length === 0) return [];

		const navPages = filterNav(roles ?? [], realmRoles)
			.filter((item) => !REFERENCE_TABLES_NAV_DUPLICATE_PATHS.has(item.path))
			.map((item) => ({
				name: item.name,
				path: item.path,
				description: NAV_ITEM_TOUR_DESCRIPTIONS[item.name],
			}));

		const referenceTablePages = REFERENCE_TABLE_PAGES.filter((page) =>
			isPathAllowed(page.path, roles, realmRoles),
		);

		const paymentGatewayPages = PAYMENT_GATEWAY_SEARCH_PAGES.filter((page) =>
			isPathAllowed(page.path, roles, realmRoles),
		);

		return [...navPages, ...referenceTablePages, ...paymentGatewayPages];
	}, [roles, realmRoles]);

	const handleClose = () => setActive(false);

	const handleSelect = (_event: SyntheticEvent, value: TSearchPage | null) => {
		if (value) {
			navigate(value.path);
		}
		handleClose();
	};

	const handleKeyDown = (event: KeyboardEvent) => {
		if (event.key === 'Escape') {
			handleClose();
		}
	};

	if (!active) {
		return (
			<EasTooltip title="Search pages">
				<IconButton color="primary" onClick={() => setActive(true)} sx={{ p: 0.5 }}>
					<SearchIcon sx={{ fontSize: '1.6rem' }} />
				</IconButton>
			</EasTooltip>
		);
	}

	return (
		<ClickAwayListener onClickAway={handleClose}>
			<Box sx={{ width: { xs: 160, sm: 220, md: 280 } }} onKeyDown={handleKeyDown}>
				<Autocomplete<TSearchPage>
					openOnFocus
					options={pages}
					filterOptions={filterOptions}
					getOptionLabel={(option) => option.name}
					isOptionEqualToValue={(option, value) => option.path === value.path}
					onChange={handleSelect}
					componentsProps={{ popper: { disablePortal: true } }}
					renderOption={(props, option) => (
						<Box component="li" {...props}>
							<Box sx={{ width: '100%' }}>
								<Typography variant="body2">{option.name}</Typography>
								{option.description && (
									<Typography variant="caption" color="text.secondary">
										{option.description}
									</Typography>
								)}
							</Box>
						</Box>
					)}
					renderInput={(params) => (
						<TextField
							{...params}
							autoFocus
							size="small"
							placeholder="Search pages…"
							InputProps={{
								...params.InputProps,
								startAdornment: (
									<InputAdornment position="start">
										<SearchIcon fontSize="small" />
									</InputAdornment>
								),
							}}
						/>
					)}
				/>
			</Box>
		</ClickAwayListener>
	);
};
